/**
 * Demo mode — frontend-only fake-data mode for visual testing.
 *
 * OFF unless the build environment enables it. There are deliberately NO
 * credentials in this file: an earlier revision hardcoded the seeded
 * super-admin's real email/password here, and because api_helper imports this
 * module on every page they shipped inside the public JS bundle. Demo
 * credentials now come from build-time env vars and must never be a real
 * account:
 *
 *   REACT_APP_DEMO_MODE=true
 *   REACT_APP_DEMO_EMAIL=demo@example.test
 *   REACT_APP_DEMO_PASSWORD=<anything; it is visible in the bundle>
 *
 * When ENABLED (all three set):
 *   1. Sign in with the demo email/password and the axios adapter
 *      short-circuits the network — no request reaches the backend, every
 *      response is fabricated by `demoDispatch`.
 *   2. All writes (POST/PUT/PATCH/DELETE) reject with a friendly
 *      "Demo mode is read-only" error.
 *   3. Every payload is obviously fake (`Demo User 1`, `0xDEM0...`,
 *      `demo-1@example.test`) so screen-shares can't leak production data.
 */

import demoData from "./demoData";

// ── Master switch + credentials (build-time env only) ──────────────────────
const envEmail = String(process.env.REACT_APP_DEMO_EMAIL || "").toLowerCase().trim();
const envPassword = String(process.env.REACT_APP_DEMO_PASSWORD || "");

export const DEMO_MODE_ENABLED =
  process.env.REACT_APP_DEMO_MODE === "true" && envEmail !== "" && envPassword !== "";

// Exposed for the fake identity below; empty when demo mode is off.
export const DEMO_EMAIL = DEMO_MODE_ENABLED ? envEmail : "";

export function isDemoCredentials(email, password) {
  if (!DEMO_MODE_ENABLED) return false;
  return String(email || "").toLowerCase().trim() === envEmail
      && String(password || "") === envPassword;
}

export function isDemoMode() {
  if (!DEMO_MODE_ENABLED) return false;
  try {
    const u = JSON.parse(localStorage.getItem("authUser") || "null");
    return Boolean(u && u.demo);
  } catch (_) {
    return false;
  }
}

// ── Identity used while in demo mode ───────────────────────────────────────
// Marked as super-admin in the FRONTEND ONLY so the user can navigate every
// page in the UI shell. Writes are blocked at the axios layer regardless.
export function makeDemoAuthUser() {
  return {
    accessToken: "DEMO_TOKEN_NOT_FOR_BACKEND",
    email: DEMO_EMAIL,
    role: "demo",
    id: 0,
    is_super_admin: true,
    demo: true,
  };
}

export function demoMeResponse() {
  return {
    status: 200,
    message: "ok (demo)",
    user: {
      id: 0,
      email: DEMO_EMAIL,
      name: "Demo Admin",
      is_super_admin: true,
      is_active: true,
      role: { id: 1, key: "demo", name: "Demo Super-admin" },
      last_login_at: new Date().toISOString(),
    },
    permissions: [], // is_super_admin bypasses
  };
}

// ── Endpoint dispatcher ────────────────────────────────────────────────────

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const WRITE_BLOCKED_MESSAGE =
  "Demo mode is read-only — sign in with real credentials to make changes.";

function blocked() {
  const err = new Error(WRITE_BLOCKED_MESSAGE);
  err.isDemoBlocked = true;
  err.response = { status: 403, data: { message: WRITE_BLOCKED_MESSAGE } };
  throw err;
}

// Routes are matched in declaration order. Each route is
//   [METHOD, regex, (match, body) => responseBody]
// The handler returns the body the page expects (the api_helper's response
// interceptor unwraps `response.data` so we return that body directly).
const ROUTES = [
  // Auth
  ["GET",  /^\/auth\/me$/,                 () => demoMeResponse()],
  ["POST", /^\/auth\/login$/,              () => ({
    status: 200,
    accessToken: "DEMO_TOKEN_NOT_FOR_BACKEND",
    email: DEMO_EMAIL,
    role: "demo",
    is_super_admin: true,
    id: 0,
    demo: true, // critical: persists into localStorage so the adapter sees isDemoMode() === true
  })],
  ["POST", /^\/auth\/logout$/,             () => ({ status: 200, message: "Logout successful (demo)" })],
  ["POST", /^\/auth\/forgot-password$/,    () => ({ status: 200, message: "If an account exists, a reset link has been sent. (demo)" })],
  ["POST", /^\/auth\/reset-password\//,    () => blocked()],
  ["POST", /^\/auth\/accept-invite\//,     () => blocked()],
  ["POST", /^\/auth\/change-password$/,    () => blocked()],

  // Dashboard / Overview
  ["GET",  /^\/dashboard\/overview$/,            () => demoData.dashboardOverview()],
  ["GET",  /^\/dashboard\/analytics\/transactions$/, () => demoData.transactionAnalytics()],
  ["GET",  /^\/dashboard\/swap-stats/,           () => ({ status: 200, timeframe: "7d", since: null, swap_count: 184, swap_volume: 92340 })],
  ["GET",  /^\/dashboard\/export$/,              () => ({ status: 200, message: "Export not available in demo mode." })],
  ["GET",  /^\/metrics\/dashboard$/,             () => demoData.metricsDashboard()],
  ["GET",  /^\/transactions\/paybis\/counts-by-flow$/, () => ({ status: 200, on_ramp: 12, off_ramp: 4 })],

  // Notifications
  ["GET",    /^\/notifications\/?$/,        () => demoData.notifications()],
  ["POST",   /^\/notifications\/?$/,        () => ({ status: 200, message: "Notification created (demo)" })],
  ["PATCH",  /^\/notifications\/\d+/,       () => ({ status: 200, message: "Notification updated (demo)" })],
  ["DELETE", /^\/notifications\/\d+/,       () => ({ status: 200, message: "Notification deleted (demo)" })],

  // Transactions + Cards
  ["GET",  /^\/transactions\/history/,     () => demoData.transactionsHistory()],
  ["GET",  /^\/transactions\/statistics/,  () => demoData.transactionsStats()],
  ["GET",  /^\/transactions\/cards\/email\//, () => ({ status: 200, cards: [] })],
  ["GET",  /^\/transactions\/cards\/\d+/,  () => ({ status: 200, card: null })],
  ["GET",  /^\/transactions\/\d+$/,        (m) => ({ status: 200, transaction: { id: m[0].split("/").pop(), status: "demo" } })],

  // Paybis
  ["GET",  /^\/transactions\/paybis\/list/,        () => demoData.paybisList()],
  ["GET",  /^\/transactions\/paybis\/history/,     () => demoData.paybisHistory()],
  ["GET",  /^\/transactions\/paybis\/statistics/,  () => demoData.paybisStats()],
  ["GET",  /^\/transactions\/paybis\/currencies/,  () => ({ status: 200, currencies: ["USD","EUR","BTC","ETH"] })],
  ["GET",  /^\/transactions\/paybis\/test/,        () => ({ status: 200, connected: false, tested_at: new Date().toISOString() })],

  // App Metrics
  ["GET",  /^\/metrics\/summary/,          () => demoData.appMetricsSummary()],
  ["GET",  /^\/metrics\/growth/,           () => demoData.appMetricsGrowth()],
  ["GET",  /^\/metrics\/platform\//,       (m) => demoData.appMetricsPlatform(m[0].split("/").pop())],

  // North Star — Weekly Engaged Wallets
  ["GET",  /^\/metrics\/wew\/summary/,            () => demoData.wewSummary()],
  ["GET",  /^\/metrics\/wew\/trend/,              () => demoData.wewTrend()],
  ["GET",  /^\/metrics\/wew\/pass-conditions/,    () => demoData.wewPass()],
  ["GET",  /^\/metrics\/wew\/retention-cohorts/,  () => demoData.wewRetention()],
  ["GET",  /^\/metrics\/wew\/countermeasures/,    () => demoData.wewCountermeasures()],

  // Alerts
  ["GET",  /^\/price-alerts\/admin\/all/,  () => demoData.alerts()],

  // Feedback
  ["GET",  /^\/feedback\/categories$/,     () => demoData.feedbackCategories()],
  ["GET",  /^\/feedback\/suggestions/,     () => demoData.feedbackSuggestions()],
  ["GET",  /^\/feedback\/bug-reports/,     () => demoData.feedbackBugReports()],
  ["GET",  /^\/feedback\/do-not-like/,     () => demoData.feedbackDoNotLike()],

  // Form Submissions
  ["GET",  /^\/admin\/submissions(?:\?|$)/, () => demoData.formSubmissionsList()],
  ["GET",  /^\/admin\/submissions\/([A-Za-z0-9-]+)$/, (m) => demoData.formSubmissionsOne(m[1])],
  ["GET",  /^\/admin\/stats$/,             () => demoData.formSubmissionsStats()],
  ["GET",  /^\/admin\/export\.csv$/,       () => "id,type,status\ndemo-1,token,new\n"],

  // User administration
  ["GET",  /^\/admin\/users$/,             () => demoData.users()],
  ["GET",  /^\/admin\/users\/\d+$/,        (m) => demoData.userById(Number(m[0].split("/").pop()))],
  ["GET",  /^\/admin\/roles$/,             () => demoData.roles()],
  ["GET",  /^\/admin\/permissions$/,       () => demoData.permissions()],
];

export function demoDispatch(method, fullUrl, body) {
  const path = String(fullUrl || "").split("?")[0].replace(/^\/api/, "");

  for (const [m, re, handler] of ROUTES) {
    if (m === method) {
      const match = path.match(re);
      if (match) return handler(match, body);
    }
  }

  // Unknown writes are blocked.
  if (WRITE_METHODS.has(method)) blocked();
  // Unknown reads return an empty success so a page never explodes.
  return { status: 200, message: "ok (demo unknown)", data: null };
}
